import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Plus, Building2, Users2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatDateTime } from '@/lib/format';

interface Block {
  id: string;
  society_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  _count?: { units: number };
}

interface BlockListResponse {
  items: Block[];
}

const CreateBlockSchema = z.object({
  name: z.string().min(1, 'Name required').max(80),
  description: z.string().max(200).optional()
});
type CreateBlockForm = z.infer<typeof CreateBlockSchema>;

export function TowersPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Block | null>(null);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<BlockListResponse>('/society/blocks')
  });

  const editingBlock = useMemo(
    () => (editingId ? query.data?.items.find((b) => b.id === editingId) ?? null : null),
    [editingId, query.data]
  );

  const form = useForm<CreateBlockForm>({
    resolver: zodResolver(CreateBlockSchema),
    defaultValues: { name: '', description: '' }
  });

  const create = useMutation({
    mutationFn: (values: CreateBlockForm) =>
      post<Block, CreateBlockForm>('/society/blocks', {
        ...values,
        description: values.description?.trim() ? values.description : undefined
      }),
    onSuccess: (b) => {
      toast.success(`Block "${b.name}" added`);
      void queryClient.invalidateQueries({ queryKey: ['society', 'blocks'] });
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: ({ id, values }: { id: string; values: CreateBlockForm }) =>
      patch<Block>(`/society/blocks/${id}`, {
        ...values,
        description: values.description?.trim() ? values.description : null
      }),
    onSuccess: (b) => {
      toast.success(`Block "${b.name}" updated`);
      void queryClient.invalidateQueries({ queryKey: ['society', 'blocks'] });
      setEditingId(null);
      setOpen(false);
      form.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/society/blocks/${id}`),
    onSuccess: () => {
      toast.success('Block removed');
      void queryClient.invalidateQueries({ queryKey: ['society', 'blocks'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  useEffect(() => {
    if (editingBlock && open) {
      form.reset({
        name: editingBlock.name,
        description: editingBlock.description ?? ''
      });
    } else if (!open && !editingId) {
      form.reset({ name: '', description: '' });
    }
  }, [editingBlock, open, editingId, form]);

  const onSubmit = (v: CreateBlockForm) => {
    if (editingId) update.mutate({ id: editingId, values: v });
    else create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
  };

  const blocks = query.data?.items ?? [];
  const totalUnits = blocks.reduce((sum, b) => sum + (b._count?.units ?? 0), 0);

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button size="sm" onClick={() => setEditingId(null)}>
          <Plus className="h-4 w-4" /> Add tower
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit tower' : 'Add tower'}</DialogTitle>
          <DialogDescription>
            A tower / block groups units together (e.g. Block A, Tower 2). Units are added separately.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-vt-4"
        >
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Block A"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.name) || undefined}
              {...form.register('name')}
            />
            {form.formState.errors.name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.name.message}</p>
            ) : (
              <p className="text-vt-12 text-vt-text-soft">Must be unique within the society.</p>
            )}
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              placeholder="Tower facing the park"
              {...form.register('description')}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editingId ? 'Saving…' : 'Adding…'}
            >
              {editingId ? 'Save changes' : 'Add tower'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Towers & Flats"
        description="Manage the physical structure of your society — towers, blocks, and units."
        actions={formDialog}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Building2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Towers / blocks</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : blocks.length}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-[color:var(--vt-info)]">
                <Users2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total units</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? <Skeleton className="h-7 w-12" /> : totalUnits}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <Building2 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Avg units / block</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {query.isLoading ? (
                    <Skeleton className="h-7 w-12" />
                  ) : blocks.length === 0 ? (
                    '—'
                  ) : (
                    Math.round(totalUnits / blocks.length)
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {query.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : query.isError ? (
              <EmptyState
                icon={<Building2 className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load towers"
                description={getErrorMessage(query.error)}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : blocks.length === 0 ? (
              <EmptyState
                icon={<Building2 className="h-6 w-6" strokeWidth={1.75} />}
                title="Structure not yet configured"
                description="Define your towers, blocks, and units to start onboarding residents."
                action={
                  <Button onClick={() => setOpen(true)}>
                    <Plus className="h-4 w-4" /> Add first tower
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tower / Block</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blocks.map((b) => {
                    const unitCount = b._count?.units ?? 0;
                    const canDelete = unitCount === 0;
                    return (
                      <TableRow key={b.id}>
                        <TableCell>
                          <div className="flex items-center gap-vt-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
                              {b.name.slice(0, 2).toUpperCase()}
                            </span>
                            <span className="font-medium text-vt-text">{b.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{b.description ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">{unitCount}</TableCell>
                        <TableCell className="text-vt-text-muted">{formatDateTime(b.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-vt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit"
                              onClick={() => {
                                setEditingId(b.id);
                                setOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!canDelete || remove.isPending}
                              title={canDelete ? 'Delete tower' : 'Remove all units first'}
                              onClick={() => setRemoveTarget(b)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Delete tower/block"
        description={
          removeTarget ? (
            <>
              Delete block <strong>{removeTarget.name}</strong>? This action cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Delete block"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />
    </>
  );
}
